/**
 * /aff/:code — affiliate link capture redirect.
 *
 * Anyone landing on /aff/ABC stores the code in localStorage (so it
 * survives navigation + signup) and is bounced to /. The persistent
 * `aff_code` is sent along with `POST /api/payments/checkout` to credit
 * the partner 30% lifetime commission on any subsequent purchase.
 */
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../config";

const ATTRIBUTION_KEY = "trp_aff_code";
const ATTRIBUTION_TIMESTAMP = "trp_aff_ts";
const COOKIE_TTL_DAYS = 90;

export default function AffiliateRedirect() {
  const { code: rawCode } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const code = (rawCode || "").trim().toLowerCase();
    if (!code) {
      navigate("/", { replace: true });
      return;
    }

    // Store with timestamp so we can expire after 90 days.
    try {
      localStorage.setItem(ATTRIBUTION_KEY, code);
      localStorage.setItem(ATTRIBUTION_TIMESTAMP, String(Date.now()));
    } catch { /* private mode */ }

    // Fire-and-forget click tracking.
    axios.post(`${API}/affiliate/track-click`, {
      code,
      landing_url: window.location.pathname,
      referrer: document.referrer || "",
    }).catch(() => { /* ignore */ });

    // Clean URL: bounce to '/' (no ?aff=) so the visitor sees a clean
    // marketing experience instead of leaked attribution params.
    navigate("/", { replace: true });
  }, [rawCode, navigate]);

  return null;
}

/**
 * Helper used by payment/checkout calls. Returns the current attributed
 * code if it's within the 90-day window, or null. Centralised so the
 * expiry logic lives in one place.
 */
export function getStoredAffCode() {
  try {
    const code = localStorage.getItem(ATTRIBUTION_KEY);
    const ts = parseInt(localStorage.getItem(ATTRIBUTION_TIMESTAMP) || "0", 10);
    if (!code || !ts) return null;
    const ageDays = (Date.now() - ts) / 86400000;
    if (ageDays > COOKIE_TTL_DAYS) {
      localStorage.removeItem(ATTRIBUTION_KEY);
      localStorage.removeItem(ATTRIBUTION_TIMESTAMP);
      return null;
    }
    return code;
  } catch {
    return null;
  }
}

/**
 * Capture `?ref=…` from any page. Used by /vs/* landings so partners can
 * link with their code without routing through /aff or /r.
 */
export function captureRefFromQuery() {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get("ref") || params.get("aff") || "").trim().toLowerCase();
    if (!ref || ref.length < 3 || ref.length > 24) return;
    localStorage.setItem(ATTRIBUTION_KEY, ref);
    localStorage.setItem(ATTRIBUTION_TIMESTAMP, String(Date.now()));
    // Fire-and-forget click tracking — non-blocking.
    fetch(`/api/affiliate/track-click`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code: ref,
        landing_url: window.location.pathname,
        referrer: document.referrer || "",
      }),
    }).catch(() => { /* ignore */ });
  } catch { /* ignore */ }
}
